import { fetchJSON as fetchAPI } from './client';

export type ExecutiveDashboard = {
  role: string;
  revenue: number;
  profitability: number;
  working_capital: number;
  forecast_accuracy: number;
  inventory_health: number;
  supplier_health: number;
  warehouse_readiness: number;
  cash_position: number;
  risk_exposure: number;
  enterprise_readiness: number;
  planning_maturity: number;
  consensus_score: number;
  business_confidence: number;
  enterprise_health_score: number;
  executive_readiness_index: number;
  pending_decisions: number;
  open_risks: number;
  open_opportunities: number;
  pending_approvals: number;
};

export type ExecutiveDecision = {
  id: string;
  title: string;
  priority: string;
  decision_owner: string;
  role: string;
  financial_impact: number;
  operational_impact: string;
  risk_assessment: string;
  confidence_score: number;
  supporting_evidence?: string;
  alternative_options?: string;
  expected_outcome: string;
  approval_status: string;
  source_module?: string;
};

export type EnterpriseRisk = {
  id: string;
  category: string;
  title: string;
  description: string;
  severity: string;
  probability: number;
  business_impact: string;
  mitigation_plan?: string;
  owner: string;
  escalation_level: string;
};

export type BusinessOpportunity = {
  id: string;
  type: string;
  title: string;
  description: string;
  estimated_value: number;
  required_investment: number;
  expected_roi: number;
  confidence: number;
  owner: string;
  target_date?: string;
  status: string;
};

export type ExecutiveBrief = {
  title: string;
  business_narrative: string;
  top_risks: string[];
  top_opportunities: string[];
  recommended_decisions: string[];
  trend_commentary: string;
  scenario_summary: string;
  action_priorities: string[];
  confidence_level: number;
  evidence_links: string[];
  auto_execute: boolean;
};

export type EnterpriseHealth = {
  enterprise_health_score: number;
  executive_readiness_index: number;
  decision_quality_index: number;
  business_confidence_index: number;
  planning_health_index: number;
  on_track: boolean;
  critical_risks: number;
  high_value_opportunities: number;
  decisions_required_today: number;
};

export type ExecutiveAlert = {
  id: string;
  title: string;
  description: string;
  severity: string;
  category: string;
  role?: string;
};

export type TimelineEvent = {
  id: string;
  event_type: string;
  title: string;
  description?: string;
  actor?: string;
  module?: string;
  occurred_at: string;
};

const API_BASE = import.meta.env.VITE_EXECUTIVECOCKPIT_API ?? '/api/v1';

function execFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const executiveCockpitApi = {
  dashboard: (role?: string) => execFetch<ExecutiveDashboard>(`/executive/dashboard${role ? `?role=${role}` : ''}`),
  brief: () => execFetch<ExecutiveBrief>('/executive/brief'),
  listDecisions: (status?: string) => execFetch<ExecutiveDecision[]>(`/executive/decisions${status ? `?status=${status}` : ''}`),
  listRisks: (category?: string) => execFetch<EnterpriseRisk[]>(`/executive/risks${category ? `?category=${category}` : ''}`),
  listOpportunities: () => execFetch<BusinessOpportunity[]>('/executive/opportunities'),
  approve: (decisionId: string) => execFetch<ExecutiveDecision>('/executive/approve', { method: 'POST', body: JSON.stringify({ decision_id: decisionId }) }),
  timeline: () => execFetch<TimelineEvent[]>('/executive/timeline'),
  health: () => execFetch<EnterpriseHealth>('/executive/health'),
  listAlerts: () => execFetch<ExecutiveAlert[]>('/executive/alerts'),
};
